import "utils/dayjs-config";
import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import AppProvider from "context/AppProvider";
import { initGA, initSentry } from "utils/analytics";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { ConfigProvider, App as AntApp } from "antd";

// antd v6 design tokens mapped to CIQ brand primitives — keep in sync with
// src/themes/ciq-default.css (--ciq-primary-900, --ciq-error-500,
// --ciq-success-text, --ciq-warning-text, --ciq-blue-600).
const antdTheme = {
  token: {
    colorPrimary: "#4448c1",
    colorError: "#ff4d4f",
    colorSuccess: "#52c41a",
    colorWarning: "#faad14",
    // antd's seed defaults colorInfo to #1677ff and does NOT derive it from
    // colorPrimary, so info/loading icons would sit a shade off --ciq-info.
    // antd also derives colorLink from this, which lines antd's links up with
    // the global `a` color src/index.css already pins to --ciq-blue-600.
    colorInfo: "#1890ff",
    borderRadius: 6,
    fontFamily: "Inter, sans-serif"
  },
  components: {
    // antd v4 tooltips were overridden to a light theme (white background, black
    // text) via src/antd-override.scss. antd v6 builds the tooltip background,
    // arrow, and text from design tokens whose runtime styles out-specify that
    // SCSS, so tooltips reverted to the default dark look. Restore the light look
    // through the v6 token API (`colorBgSpotlight` colors the tooltip background
    // and arrow).
    Tooltip: {
      colorBgSpotlight: "#ffffff", // tooltip background + arrow
      colorTextLightSolid: "#000000", // tooltip text
      maxWidth: 320 // matches the old .ant-tooltip-container max-width
    }
  }
};

/*
 * antd's static methods — `Modal.confirm/info/warning/error/success`, `message`
 * and `notification` — render into their own detached root, so they never see
 * the <ConfigProvider> below and fall back to antd's default seed
 * (colorPrimary #1677ff). The app's plain CSS still painted those buttons
 * brand-indigo at rest, so the mismatch only showed on hover:
 * `antd/lib/modal/confirm.js` re-wraps the dialog in its own ConfigProvider
 * built from `globalConfig().getTheme()`, and with no global theme its
 * colorPrimaryHover resolved to #4096ff — the blue reported on the linking
 * page's "Unsaved Changes" dialog (measured rgb(64,150,255) vs rgb(105,112,207)
 * for the same button inside the React tree).
 *
 * `ConfigProvider.config` populates that global theme, so the ~41 static Modal
 * call sites across the app pick up the same tokens as everything else. Must run
 * before the first static call, hence module scope.
 */
ConfigProvider.config({ theme: antdTheme });

initGA();
initSentry();

const container = document.getElementById("root");
const root = createRoot(container!);
root.render(
  <React.StrictMode>
    <AppProvider>
      <ConfigProvider theme={antdTheme}>
        <AntApp className="h-full">
          <App />
        </AntApp>
      </ConfigProvider>
    </AppProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
